import { useCallback, useMemo } from "react";
import { buildReasonTaxClassMap } from "../utils/reasonTaxClass";
import { DEFAULT_DISTANCE_DECIMALS, DistanceDecimals, toDistanceDecimals } from "@/constants/distanceDecimals";
import { DEFAULT_MAP_TILE_ID, MAP_TILE_LAYERS, MapTileId } from "@/constants/MapTiles";
import { ReasonTaxClass } from "@/constants/ReasonTaxClasses";
import {
  AuditorGroup,
  useAuditorGroupSettings,
  useSaveAuditorGroupSettings,
} from "@/hooks/addins/useAuditorGroupSettings";
import {
  useReasonTaxClassSettings,
  useSaveReasonTaxClassSettings,
} from "@/hooks/addins/useReasonTaxClassSettings";
import { useUserState } from "@/hooks/userState/useUserState";

/** The period summary and the column filter row start collapsed, no map width is stored. */
const DEFAULT_SETTINGS = {
  distanceDecimals: DEFAULT_DISTANCE_DECIMALS as number,
  tileId: DEFAULT_MAP_TILE_ID as string,
  includeArchived: false,
  summaryOpen: false,
  showHeaderFilters: false,
  mapWidth: 0,
};

function toTileId(value: string): MapTileId {
  return MAP_TILE_LAYERS.find(layer => layer.id === value)?.id ?? DEFAULT_MAP_TILE_ID;
}

const EMPTY_GROUPS: AuditorGroup[] = [];

/** Display settings are the user's own; the tax class assignment and the auditors' vehicle types are shared by the whole database. */
export function useAuditSettings() {
  const { value, setValue } = useUserState("logbookAudit:settings", DEFAULT_SETTINGS);
  const { data: taxClassSettings } = useReasonTaxClassSettings();
  const saveTaxClasses = useSaveReasonTaxClassSettings();
  const { data: auditorGroupSettings } = useAuditorGroupSettings();
  const saveAuditorGroups = useSaveAuditorGroupSettings();

  const overrides = useMemo(() => taxClassSettings?.overrides ?? {}, [taxClassSettings]);
  const taxClasses = useMemo(() => buildReasonTaxClassMap(overrides), [overrides]);

  const setDistanceDecimals = useCallback(
    (distanceDecimals: DistanceDecimals) => setValue(current => ({ ...current, distanceDecimals })),
    [setValue],
  );
  const setTileId = useCallback((tileId: MapTileId) => setValue(current => ({ ...current, tileId })), [setValue]);
  const setIncludeArchived = useCallback(
    (includeArchived: boolean) => setValue(current => ({ ...current, includeArchived })),
    [setValue],
  );
  const setSummaryOpen = useCallback(
    (summaryOpen: boolean) => setValue(current => ({ ...current, summaryOpen })),
    [setValue],
  );
  const setMapWidth = useCallback((mapWidth: number) => setValue(current => ({ ...current, mapWidth })), [setValue]);

  const setShowHeaderFilters = useCallback(
    (showHeaderFilters: boolean) => setValue(current => ({ ...current, showHeaderFilters })),
    [setValue],
  );

  const setReasonTaxClass = useCallback(
    (reason: string, taxClass: ReasonTaxClass) => {
      saveTaxClasses.mutate({ overrides: { ...overrides, [reason]: taxClass } });
    },
    [saveTaxClasses, overrides],
  );

  const resetReasonTaxClasses = useCallback(() => {
    saveTaxClasses.mutate({ overrides: {} });
  }, [saveTaxClasses]);

  const setAuditorGroups = useCallback(
    (groups: AuditorGroup[]) => {
      saveAuditorGroups.mutate({ id: auditorGroupSettings?.id, groups });
    },
    [saveAuditorGroups, auditorGroupSettings?.id],
  );

  return {
    distanceDecimals: toDistanceDecimals(value.distanceDecimals),
    setDistanceDecimals,
    tileId: toTileId(value.tileId),
    setTileId,
    includeArchived: value.includeArchived,
    setIncludeArchived,
    summaryOpen: value.summaryOpen,
    setSummaryOpen,
    mapWidth: value.mapWidth,
    setMapWidth,
    showHeaderFilters: value.showHeaderFilters,
    setShowHeaderFilters,
    taxClasses,
    taxClassOverrides: overrides,
    setReasonTaxClass,
    resetReasonTaxClasses,
    isSavingTaxClasses: saveTaxClasses.isPending,
    /** Groups an administrator opened for the auditors; empty or none enabled means every vehicle. */
    auditorGroups: auditorGroupSettings?.groups ?? EMPTY_GROUPS,
    setAuditorGroups,
  };
}

export type AuditSettings = ReturnType<typeof useAuditSettings>;
